import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui-helpers";
import { Quote, X, Cake, PartyPopper } from "lucide-react";

function greeting(d = new Date()) {
  const h = d.getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function parseMD(s) {
  if (!s || typeof s !== "string") return null;
  const m = s.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { y: +m[1], mo: +m[2], d: +m[3] };
}

// Personal greetings for the day, based on the logged-in person's birthday / work anniversary
// (admin-set). Matched on month + day against today's local date.
function celebrations(user, today) {
  const out = [];
  const mo = today.getMonth() + 1, d = today.getDate(), y = today.getFullYear();
  const bd = parseMD(user?.birthday);
  if (bd && bd.mo === mo && bd.d === d) out.push({ kind: "birthday", text: "Happy birthday!" });
  const wa = parseMD(user?.work_anniversary);
  if (wa && wa.mo === mo && wa.d === d) {
    const years = y - wa.y;
    if (years >= 1) out.push({ kind: "anniversary", text: years === 1 ? "Happy 1-year work anniversary!" : `Happy ${years}-year work anniversary!` });
  }
  return out;
}

// Daily welcome banner: time-aware greeting by first name + today's date + the team's Quote of the Day.
// The quote is the same for everyone each calendar day (server rotation). The banner can be hidden for
// the current day only, remembered per-browser; it returns the next day with the new quote.
export function WelcomeBanner() {
  const { user } = useAuth();
  const [quote, setQuote] = useState(null); // null = loading, false = none, object = quote
  const today = new Date();
  const dateKey = today.toISOString().slice(0, 10);
  const hideKey = `qotd_hidden_${dateKey}`;
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(hideKey) === "1"; } catch { return false; }
  });

  useEffect(() => {
    api.get("/quote-of-the-day").then((r) => setQuote(r.data?.quote || false)).catch(() => setQuote(false));
  }, []);

  const hide = () => {
    try { localStorage.setItem(hideKey, "1"); } catch { /* ignore */ }
    setHidden(true);
  };

  if (hidden) return null;
  if (quote === null) return null; // still loading — avoid a flash

  const first = (user?.name || "").trim().split(" ")[0] || "there";
  const dateLabel = today.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
  const cels = celebrations(user, today);
  if (!quote && cels.length === 0) return null; // nothing to show → hide the banner

  return (
    <Card className="p-5 mb-6 relative bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/10" data-testid="welcome-banner">
      <button onClick={hide} data-testid="welcome-banner-hide" title="Hide for today"
        className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition-colors">
        <X className="h-4 w-4" />
      </button>
      <h2 className="font-head text-xl sm:text-2xl font-extrabold text-slate-900" data-testid="welcome-greeting">{greeting()}, {first}</h2>
      <p className="text-xs text-slate-500 mt-0.5" data-testid="welcome-date">{dateLabel}</p>
      {cels.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {cels.map((c) => (
            <div key={c.kind} data-testid={`celebration-${c.kind}`} className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-800 rounded-full px-3 py-1 text-sm font-semibold">
              {c.kind === "birthday" ? <Cake className="h-4 w-4" /> : <PartyPopper className="h-4 w-4" />}
              {c.text}
            </div>
          ))}
        </div>
      )}
      {quote && (
        <div className="mt-3 flex items-start gap-2 max-w-2xl">
          <Quote className="h-4 w-4 text-primary/60 shrink-0 mt-1.5" />
          <div className="min-w-0">
            <p className="text-base sm:text-lg text-slate-700 font-light italic leading-snug" data-testid="welcome-quote-text">{quote.text}</p>
            {quote.author && <p className="text-xs text-slate-500 mt-1" data-testid="welcome-quote-author">— {quote.author}</p>}
          </div>
        </div>
      )}
    </Card>
  );
}
